import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Form, Dropdown } from 'semantic-ui-react';
import { uniqBy } from 'lodash-es';

import { useDebounce } from '../hooks/useDebounce';
import { QUERY_SEARCH_DEBOUNCE_IN_MS } from '../config/query';

type Value =
  | undefined
  | string
  | number
  | boolean
  | (string | number | boolean)[];

type Option = {
  text: string;
  value: string | number | boolean;
};

type Props = {
  /**
   * Unique identifier for the field. This will be used for caching the requests.
   */
  identifier: string;
  /**
   * Label for the dropdown field.
   */
  label?: string;
  /**
   * Whether the field is required.
   */
  required?: boolean;
  /**
   * The current value of the dropdown.
   * If multiple is true, this should be an array of values.
   */
  value?: Value;
  /**
   * The current value(s) of the dropdown.
   * If `multiple` is true, this should be an array of values.
   * If `multiple` is false, this should be a single value.
   */
  multiple?: boolean;
  /**
   * Whether the dropdown is searchable.
   */
  searchable?: boolean;
  /**
   * Callback function to handle value changes.
   * It will be called with the new value(s) when the user selects an option.
   */
  onChange: (value: Value) => void;
  /**
   * Function to fetch the initial options for the dropdown.
   * This will be called once when the component mounts.
   * That can be useful if you want to preload some options before the user starts searching.
   */
  initialOptionsQueryFn: (value: Value) => Promise<Option[]>;
  /**
   * Function to fetch options based on the search term.
   * This will be called whenever the user types in the search input.
   * It should return an array of options that match the search term.
   */
  searchOptionsQueryFn: (searchTerm?: string) => Promise<Option[]>;
};

/**
 * AsyncDropdownField is a reusable dropdown component that fetches options asynchronously.
 * It supports multiple selections, search functionality, and can handle both initial and search queries.
 */
export const AsyncDropdownField = ({
  label,
  value,
  identifier,
  required = false,
  multiple = false,
  searchable = false,
  onChange,
  initialOptionsQueryFn,
  searchOptionsQueryFn,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(
    searchTerm,
    QUERY_SEARCH_DEBOUNCE_IN_MS
  );

  const {
    data: initialOptions = [],
    isLoading: isLoadingInitialOptions,
    refetch: refetchInitialOptions,
  } = useQuery({
    queryKey: [identifier],
    queryFn: async () => initialOptionsQueryFn?.(value),
  });

  const { data: searchOptions = [], isLoading: isLoadingSearchOptions } =
    useQuery({
      queryKey: [identifier, debouncedSearchTerm],
      queryFn: async () => searchOptionsQueryFn(debouncedSearchTerm),
      enabled: searchable,
    });

  const isLoading = isLoadingInitialOptions || isLoadingSearchOptions;
  const options = uniqBy([...initialOptions, ...searchOptions], 'value');

  // Although useEffect is not recommended when not dealing with async operations,
  // that's the only way found to ensure we will refetch options when the value changes
  // and the options do not cover the new value.
  // For example, a new entity is created and passed in the value prop but it has not been fetched yet.
  useEffect(() => {
    if (
      Array.isArray(value) &&
      value.some((v) => !options.some((o) => o.value === v))
    ) {
      refetchInitialOptions();
    }
  }, [value]);

  return (
    <Form.Field required={required}>
      {label && <label>{label}</label>}
      <Dropdown
        multiple={multiple}
        search={searchable}
        selection
        options={options}
        value={value}
        onChange={(_, { value }) => onChange?.(value)}
        onSearchChange={(_, { searchQuery }) => setSearchTerm(searchQuery)}
        loading={isLoading}
        noResultsMessage={null}
      />
    </Form.Field>
  );
};
